/**
 * 人員變更影響分析工具 | Person Change Impact Analyzer
 * 分析人員資料變更對訂單、工單的影響 | Analyze impact of person changes on orders and work orders
 */

import { Person } from '@/types/person';
import { SubWorkOrder, MainWorkOrder, Order } from '@/types/order';

/**
 * 受影響的訂單資訊 | Affected Order Info
 */
export interface AffectedOrder {
  orderId: string;
  orderProductName: string;
  orderCustomer: string;
}

/**
 * 受影響的工單資訊 | Affected Work Order Info
 */
export interface AffectedWorkOrder {
  mainWorkOrderId: string;
  mainWorkOrderName: string;
  subWorkOrders: Array<{
    subWorkOrderId: string;
    processName: string;
    processCode: string;
  }>;
}

/**
 * 人員變更影響分析結果 | Person Change Impact Analysis Result
 */
export interface PersonImpact {
  personId: string;
  personName: string;
  hasIdChange: boolean;
  oldId?: string;
  newId?: string;

  // 受影響的訂單 | Affected orders
  affectedOrders: AffectedOrder[];
  // 受影響的工單 | Affected work orders
  affectedWorkOrders: AffectedWorkOrder[];

  // 統計資訊 | Statistics
  totalOrderCount: number;
  totalMainWorkOrderCount: number;
  totalSubWorkOrderCount: number;

  // 變更的欄位 | Changed fields
  changedFields: string[];
}

/**
 * 人員變更資料 | Person Change Data
 */
export interface PersonChange {
  oldPerson: Person;
  newPerson: Person;
}

/**
 * 分析人員變更的影響 | Analyze impact of person changes
 * @param change - 人員變更資料 | Person change data
 * @param allOrders - 所有訂單 | All orders
 * @param allMainWorkOrders - 所有主工單 | All main work orders
 * @param allSubWorkOrders - 所有子工單 | All sub work orders
 * @returns 影響分析結果 | Impact analysis result
 */
export const analyzePersonImpact = (
  change: PersonChange,
  allOrders: Order[],
  allMainWorkOrders: MainWorkOrder[],
  allSubWorkOrders: SubWorkOrder[]
): PersonImpact => {
  const { oldPerson, newPerson } = change;

  // 檢查 ID 是否變更 | Check if ID changed
  const hasIdChange = oldPerson.id !== newPerson.id;

  // 找出所有變更的欄位 | Find all changed fields
  const changedFields = getChangedFields(oldPerson, newPerson);

  // 收集受影響的訂單和工單 | Collect affected orders and work orders
  const affectedOrders: AffectedOrder[] = [];
  const affectedWorkOrdersMap = new Map<string, AffectedWorkOrder>();

  // 遍歷所有子工單，檢查是否分配給此人員 | Iterate through all sub work orders
  allSubWorkOrders.forEach((swo) => {
    // 檢查是否分配給舊 ID | Check if assigned to old ID
    const isAssignedToOldId = swo.assignedPersons?.includes(oldPerson.id);

    if (isAssignedToOldId) {
      // 找到對應的主工單 | Find corresponding main work order
      const mwo = allMainWorkOrders.find(mwo => mwo.id === swo.mainOrderId);
      if (mwo) {
        // 找到對應的訂單 | Find corresponding order
        const order = allOrders.find(o => o.id === mwo.orderId);
        if (order) {
          // 加入受影響的訂單 | Add to affected orders
          if (!affectedOrders.find(ao => ao.orderId === order.id)) {
            affectedOrders.push({
              orderId: order.id,
              orderProductName: order.productName,
              orderCustomer: order.customer,
            });
          }

          // 加入受影響的工單 | Add to affected work orders
          if (!affectedWorkOrdersMap.has(mwo.id)) {
            affectedWorkOrdersMap.set(mwo.id, {
              mainWorkOrderId: mwo.id,
              mainWorkOrderName: `主工單 ${mwo.id}`,
              subWorkOrders: [],
            });
          }

          const affectedMwo = affectedWorkOrdersMap.get(mwo.id)!;
          affectedMwo.subWorkOrders.push({
            subWorkOrderId: swo.id,
            processName: swo.processName,
            processCode: swo.processCode,
          });
        }
      }
    }
  });

  const affectedWorkOrders = Array.from(affectedWorkOrdersMap.values());
  const totalSubWorkOrderCount = affectedWorkOrders.reduce(
    (sum, mwo) => sum + mwo.subWorkOrders.length,
    0
  );

  return {
    personId: oldPerson.id,
    personName: oldPerson.name,
    hasIdChange,
    oldId: hasIdChange ? oldPerson.id : undefined,
    newId: hasIdChange ? newPerson.id : undefined,
    affectedOrders,
    affectedWorkOrders,
    totalOrderCount: affectedOrders.length,
    totalMainWorkOrderCount: affectedWorkOrders.length,
    totalSubWorkOrderCount,
    changedFields,
  };
};

/**
 * 比較兩個人員物件，找出變更的欄位 | Compare two person objects, find changed fields
 * @param oldPerson - 舊人員資料 | Old person data
 * @param newPerson - 新人員資料 | New person data
 * @returns 變更的欄位名稱陣列 | Array of changed field names
 */
export const getChangedFields = (
  oldPerson: Person,
  newPerson: Person
): string[] => {
  const changedFields: string[] = [];

  // 欄位名稱對照表 | Field name mapping
  const fieldNames: Record<keyof Person, string> = {
    id: '工號',
    name: '姓名',
    email: '電子郵件',
    plants: '廠別',
    department: '部門',
    roles: '角色',
    shiftType: '班別',
    status: '狀態',
    dailyHours: '每日工時',
    skills: '技能',
    hireDate: '僱用日期',
  };

  // 逐一比較欄位 | Compare fields one by one
  (Object.keys(fieldNames) as Array<keyof Person>).forEach((key) => {
    const oldValue = oldPerson[key];
    const newValue = newPerson[key];

    // 處理陣列類型 | Handle array types
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      if (JSON.stringify(oldValue.sort()) !== JSON.stringify(newValue.sort())) {
        changedFields.push(fieldNames[key]);
      }
    }
    // 處理一般值 | Handle regular values
    else if (oldValue !== newValue) {
      changedFields.push(fieldNames[key]);
    }
  });

  return changedFields;
};

/**
 * 格式化影響分析結果為摘要文字 | Format impact analysis result as summary text
 * @param impact - 影響分析結果 | Impact analysis result
 * @returns 摘要文字 | Summary text
 */
export const formatImpactSummary = (impact: PersonImpact): string => {
  const parts: string[] = [];

  if (impact.hasIdChange) {
    parts.push(
      `ID 將從「${impact.oldId}」變更為「${impact.newId}」`
    );
  }

  if (impact.totalOrderCount > 0) {
    parts.push(
      `影響 ${impact.totalOrderCount} 個訂單、${impact.totalMainWorkOrderCount} 個主工單、${impact.totalSubWorkOrderCount} 個子工單`
    );
  }

  if (impact.changedFields.length > 0) {
    parts.push(`變更欄位：${impact.changedFields.join('、')}`);
  }

  return parts.join('，');
};

/**
 * 生成影響分析的 HTML 報告 | Generate HTML report for impact analysis
 * @param impact - 影響分析結果 | Impact analysis result
 * @returns HTML 字串 | HTML string
 */
export const generateImpactReport = (impact: PersonImpact): string => {
  let html = '<div class="impact-report">';

  // ID 變更警告 | ID change warning
  if (impact.hasIdChange) {
    html += `
      <div class="impact-warning">
        <strong>⚠️ ID 變更警告</strong><br/>
        工號將從「${impact.oldId}」變更為「${impact.newId}」<br/>
        系統將自動更新所有相關工單的分配記錄
      </div>
    `;
  }

  // 統計摘要 | Statistics summary
  html += `
    <div class="impact-summary">
      <strong>📊 影響統計</strong><br/>
      • 影響訂單：${impact.totalOrderCount} 個<br/>
      • 影響主工單：${impact.totalMainWorkOrderCount} 個<br/>
      • 影響子工單：${impact.totalSubWorkOrderCount} 個<br/>
    </div>
  `;

  // 變更欄位 | Changed fields
  if (impact.changedFields.length > 0) {
    html += `
      <div class="impact-fields">
        <strong>📝 變更欄位</strong><br/>
        ${impact.changedFields.map(f => `• ${f}`).join('<br/>')}
      </div>
    `;
  }

  // 受影響的訂單 | Affected orders
  if (impact.affectedOrders.length > 0) {
    html += `
      <div class="impact-orders">
        <strong>📦 受影響的訂單</strong><br/>
        ${impact.affectedOrders.map(order =>
          `• ${order.orderProductName} (${order.orderCustomer}) [${order.orderId}]`
        ).join('<br/>')}
      </div>
    `;
  }

  // 受影響的工單 | Affected work orders
  if (impact.affectedWorkOrders.length > 0) {
    html += `
      <div class="impact-workorders">
        <strong>🔧 受影響的工單</strong><br/>
        ${impact.affectedWorkOrders.map(mwo => `
          <div class="impact-mwo">
            • ${mwo.mainWorkOrderName}<br/>
            &nbsp;&nbsp;子工單：${mwo.subWorkOrders.map(swo =>
              `${swo.processName} (${swo.processCode})`
            ).join('、')}
          </div>
        `).join('')}
      </div>
    `;
  }

  html += '</div>';
  return html;
};
